/**
 * Pure helpers for BioSDK REST path selection (`/mosip`, `/iso`, `/raw` namespaces).
 * Kept separate from `config.js` for unit testing without `window` / env.
 */

export const trimTrailingSlash = (u) => (u || '').replace(/\/$/, '');

export const buildBiosdkRawBaseUrl = (biosdkBaseUrl) => `${trimTrailingSlash(biosdkBaseUrl)}/raw`;
export const buildBiosdkMosipBaseUrl = (biosdkBaseUrl) => `${trimTrailingSlash(biosdkBaseUrl)}/mosip`;
export const buildBiosdkIsoBaseUrl = (biosdkBaseUrl) => `${trimTrailingSlash(biosdkBaseUrl)}/iso`;
export const buildBiosdkMinexBaseUrl = (biosdkBaseUrl) => `${trimTrailingSlash(biosdkBaseUrl)}/minex`;

export const API_VARIANT_MOSIP = 'mosip';
export const API_VARIANT_ISO = 'iso';
export const API_VARIANT_RAW = 'raw';
export const API_VARIANT_NIST = 'nist';
export const API_VARIANT_MINEX = 'minex';

export const MODALITY_FACE = 'face';
export const MODALITY_FINGER = 'finger';
export const MODALITY_IRIS = 'iris';

export const NIST_SUPPORTED_MODALITIES = new Set([MODALITY_FACE, MODALITY_IRIS]);
export const MINEX_SUPPORTED_MODALITIES = new Set([MODALITY_FINGER]);

export const PAGE_OP_EXTRACT = 'extract';
export const PAGE_OP_MATCH = 'match';
export const PAGE_OP_QUALITY = 'quality';

/** @deprecated use API_VARIANT_* constants */
export const ISO_NAMESPACE_MOSIP = API_VARIANT_MOSIP;
/** @deprecated use API_VARIANT_* constants */
export const ISO_NAMESPACE_ISO = API_VARIANT_ISO;
export const ISO_NAMESPACE_STORAGE_KEY = 'biosdk.isoNamespace';

export const EXTRACTION_MODE_MOSIP = 'mosip';
export const EXTRACTION_MODE_PROCESSED = 'processed';

export const RAW_FORMATS_COMMON = ['jpg', 'jpeg', 'png', 'bmp'];

export const CONTEXT_FACE_EXTRACT_MOSIP = 'face_extract_mosip';
export const CONTEXT_FACE_EXTRACT_PROCESSED = 'face_extract_processed';
export const CONTEXT_FACE_MATCH = 'face_match';
export const CONTEXT_FACE_QUALITY = 'face_quality';
export const CONTEXT_FINGER_EXTRACT = 'finger_extract';
export const CONTEXT_FINGER_MATCH = 'finger_match';
export const CONTEXT_FINGER_QUALITY = 'finger_quality';
export const CONTEXT_IRIS_EXTRACT = 'iris_extract';
export const CONTEXT_IRIS_MATCH = 'iris_match';
export const CONTEXT_IRIS_QUALITY = 'iris_quality';

const toFormatSet = (formats) => new Set(formats.map((f) => f.toLowerCase()));

export const RAW_FORMATS_BY_CONTEXT = {
  [CONTEXT_FACE_EXTRACT_MOSIP]: toFormatSet([...RAW_FORMATS_COMMON, 'jp2']),
  [CONTEXT_FACE_EXTRACT_PROCESSED]: toFormatSet(RAW_FORMATS_COMMON),
  [CONTEXT_FACE_MATCH]: toFormatSet(RAW_FORMATS_COMMON),
  [CONTEXT_FACE_QUALITY]: toFormatSet(RAW_FORMATS_COMMON),
  [CONTEXT_FINGER_EXTRACT]: toFormatSet([...RAW_FORMATS_COMMON, 'jp2']),
  [CONTEXT_FINGER_MATCH]: toFormatSet([...RAW_FORMATS_COMMON, 'wsq']),
  [CONTEXT_FINGER_QUALITY]: toFormatSet([...RAW_FORMATS_COMMON, 'wsq']),
  [CONTEXT_IRIS_EXTRACT]: toFormatSet([...RAW_FORMATS_COMMON, 'jp2']),
  [CONTEXT_IRIS_MATCH]: toFormatSet(RAW_FORMATS_COMMON),
  [CONTEXT_IRIS_QUALITY]: toFormatSet(RAW_FORMATS_COMMON),
};

const PLAIN_IMAGE_EXTENSIONS = new Set(
  Object.values(RAW_FORMATS_BY_CONTEXT).flatMap((set) => [...set])
);

const UNSUPPORTED_IN_CONTEXT = {
  [CONTEXT_FACE_MATCH]: new Set(['jp2']),
  [CONTEXT_FACE_QUALITY]: new Set(['jp2']),
  [CONTEXT_FINGER_MATCH]: new Set(['jp2']),
  [CONTEXT_FINGER_QUALITY]: new Set(['jp2']),
  [CONTEXT_IRIS_MATCH]: new Set(['jp2']),
  [CONTEXT_IRIS_QUALITY]: new Set(['jp2']),
  [CONTEXT_FACE_EXTRACT_PROCESSED]: new Set(['jp2']),
};

export const ISO_RECORD_LABEL =
  'ISO19794 record (BIR bytes; e.g. .iso, .bin — extension not strict)';

export const RAW_BLOCKED_MESSAGE =
  'Raw variant accepts plain images only. For ISO19794 records (e.g. .iso, .bin), switch to MOSIP or ISO.';

export const NIST_FINGER_BLOCKED_MESSAGE =
  'NIST variant supports Face and Iris only. Finger FRVT certification is coming later.';

export const NIST_QUALITY_BLOCKED_MESSAGE =
  'NIST variant is for FRVT extract and match (1:1 and 1:N gallery[]). Quality checks are not on the certification path.';

export const NIST_PLAIN_IMAGE_ONLY_MESSAGE =
  'NIST variant accepts plain images only (FRVT extract path). Upload JPG, PNG, or BMP.';

export const MINEX_FACE_IRIS_BLOCKED_MESSAGE =
  'MINEX variant supports Fingerprint only.';

export const MINEX_QUALITY_BLOCKED_MESSAGE =
  'MINEX variant is for extract and match (ANSI 378). Quality is not on the certification path.';

export const MINEX_ANSI_TEMPLATE_LABEL =
  'ANSI INCITS 378-2004 template (.bin)';

export const MINEX_FORMATS_FINGER = ['jpg', 'jpeg', 'png', 'bmp', 'wsq', 'bin'];

export const isMinexAnsiTemplateExtension = (ext) =>
  normalizeFileExtension(ext) === 'bin';

export const normalizeApiVariant = (value) => {
  if (value === API_VARIANT_ISO) return API_VARIANT_ISO;
  if (value === API_VARIANT_RAW) return API_VARIANT_RAW;
  if (value === API_VARIANT_NIST) return API_VARIANT_NIST;
  if (value === API_VARIANT_MINEX) return API_VARIANT_MINEX;
  return API_VARIANT_MOSIP;
};

export const isNistModalitySupported = (modality) =>
  NIST_SUPPORTED_MODALITIES.has(modality);

export const isMinexModalitySupported = (modality) =>
  MINEX_SUPPORTED_MODALITIES.has(modality);

export const MODALITY_ORDER = [MODALITY_FACE, MODALITY_FINGER, MODALITY_IRIS];

export const NAV_SECTION_EXTRACTOR = 'extractor';
export const NAV_SECTION_MATCHER = 'matcher';
export const NAV_SECTION_QUALITY = 'quality';

const MODALITY_ROUTE_BY_SECTION = {
  [NAV_SECTION_EXTRACTOR]: {
    [MODALITY_FACE]: '/extractor',
    [MODALITY_FINGER]: '/extractor/finger',
    [MODALITY_IRIS]: '/extractor/iris',
  },
  [NAV_SECTION_MATCHER]: {
    [MODALITY_FACE]: '/matcher',
    [MODALITY_FINGER]: '/matcher/finger',
    [MODALITY_IRIS]: '/matcher/iris',
  },
  [NAV_SECTION_QUALITY]: {
    [MODALITY_FACE]: '/quality',
    [MODALITY_FINGER]: '/quality/finger',
    [MODALITY_IRIS]: '/quality/iris',
  },
};

export const isModalitySupportedForVariant = (apiVariant, modality) => {
  const variant = normalizeApiVariant(apiVariant);
  if (variant === API_VARIANT_NIST) return isNistModalitySupported(modality);
  if (variant === API_VARIANT_MINEX) return isMinexModalitySupported(modality);
  return true;
};

export const isQualitySupportedForVariant = (apiVariant) => {
  const variant = normalizeApiVariant(apiVariant);
  return variant !== API_VARIANT_NIST && variant !== API_VARIANT_MINEX;
};

export const listSupportedModalities = (apiVariant) =>
  MODALITY_ORDER.filter((modality) => isModalitySupportedForVariant(apiVariant, modality));

export const resolveModalityRoute = (section, modality) => {
  const routes = MODALITY_ROUTE_BY_SECTION[section];
  if (!routes) return null;
  return routes[modality] || null;
};

export const parseModalityFromPath = (pathname) => {
  const path = String(pathname || '');
  if (path.includes('/finger')) return MODALITY_FINGER;
  if (path.includes('/iris')) return MODALITY_IRIS;
  return MODALITY_FACE;
};

export const parseNavSectionFromPath = (pathname) => {
  const path = String(pathname || '');
  if (path.startsWith('/matcher')) return NAV_SECTION_MATCHER;
  if (path.startsWith('/quality')) return NAV_SECTION_QUALITY;
  if (path.startsWith('/extractor')) return NAV_SECTION_EXTRACTOR;
  return null;
};

export const firstSupportedRoute = (section, apiVariant) => {
  const modalities = listSupportedModalities(apiVariant);
  for (const modality of modalities) {
    const route = resolveModalityRoute(section, modality);
    if (route) return route;
  }
  return null;
};

export const resolveNistOperationPath = (modality, pageOperation) => {
  if (pageOperation === PAGE_OP_MATCH) return 'match';
  if (pageOperation === PAGE_OP_QUALITY) return 'process';
  if (modality === MODALITY_FACE) return 'extract-processed-template';
  if (modality === MODALITY_IRIS) return 'extract-template';
  return 'extract-template';
};

export const buildBiosdkNistRouteUrl = (biosdkBaseUrl, operationPath) =>
  `${buildBiosdkRawBaseUrl(biosdkBaseUrl)}/${String(operationPath || '').replace(/^\//, '')}`;

export const resolveNistRouteUrl = (biosdkBaseUrl, modality, pageOperation) => {
  const operationPath = resolveNistOperationPath(modality, pageOperation);
  return {
    url: buildBiosdkNistRouteUrl(biosdkBaseUrl, operationPath),
    namespace: API_VARIANT_NIST,
    isRaw: true,
    operationPath,
  };
};

export const resolveNistVariantStatus = (
  biosdkBaseUrl,
  modality,
  pageOperation,
  fileExtensions = [],
  contextKey
) => {
  const route = resolveNistRouteUrl(biosdkBaseUrl, modality, pageOperation);
  const exts = fileExtensions.map(normalizeFileExtension).filter(Boolean);
  const endpointPath = route.url.replace(trimTrailingSlash(biosdkBaseUrl), '');
  const unsupportedModality = !isNistModalitySupported(modality);
  const qualityPage = pageOperation === PAGE_OP_QUALITY;
  const isNistBlocked = unsupportedModality || qualityPage;
  let blockedReason = null;
  if (qualityPage) blockedReason = NIST_QUALITY_BLOCKED_MESSAGE;
  else if (unsupportedModality) blockedReason = NIST_FINGER_BLOCKED_MESSAGE;

  const hasPlainImage = exts.some((e) => isPlainImageForContext(e, contextKey));
  const hasIsoRecord = exts.some((e) => resolveInputKind(e, contextKey) === 'iso_record');

  return {
    ...route,
    isMosipVariant: false,
    isIsoVariant: false,
    isMosipBlocked: false,
    isRawVariant: false,
    isRawBlocked: false,
    isNistVariant: !isNistBlocked && (exts.length === 0 || hasPlainImage || hasIsoRecord),
    isNistBlocked,
    label: isNistBlocked ? 'NIST variant (blocked)' : 'NIST variant',
    endpointPath,
    mosipVariantYesNo: false,
    blockedReason,
  };
};

export const resolveMinexOperationPath = (pageOperation) => {
  if (pageOperation === PAGE_OP_MATCH) return 'match';
  if (pageOperation === PAGE_OP_QUALITY) return 'process';
  return 'extract-template';
};

export const buildBiosdkMinexRouteUrl = (biosdkBaseUrl, operationPath) =>
  `${buildBiosdkMinexBaseUrl(biosdkBaseUrl)}/${String(operationPath || '').replace(/^\//, '')}`;

export const resolveMinexRouteUrl = (biosdkBaseUrl, modality, pageOperation) => {
  const operationPath = resolveMinexOperationPath(pageOperation);
  return {
    url: buildBiosdkMinexRouteUrl(biosdkBaseUrl, operationPath),
    namespace: API_VARIANT_MINEX,
    isRaw: false,
    operationPath,
  };
};

export const resolveMinexVariantStatus = (
  biosdkBaseUrl,
  modality,
  pageOperation,
  fileExtensions = [],
  contextKey
) => {
  const route = resolveMinexRouteUrl(biosdkBaseUrl, modality, pageOperation);
  const exts = fileExtensions.map(normalizeFileExtension).filter(Boolean);
  const endpointPath = route.url.replace(trimTrailingSlash(biosdkBaseUrl), '');
  const unsupportedModality = !isMinexModalitySupported(modality);
  const qualityPage = pageOperation === PAGE_OP_QUALITY;
  const isMinexBlocked = unsupportedModality || qualityPage;
  let blockedReason = null;
  if (qualityPage) blockedReason = MINEX_QUALITY_BLOCKED_MESSAGE;
  else if (unsupportedModality) blockedReason = MINEX_FACE_IRIS_BLOCKED_MESSAGE;

  const hasPlainImage = exts.some((e) => isPlainImageForContext(e, contextKey));
  const hasAnsiTemplate = exts.some((e) => isMinexAnsiTemplateExtension(e));

  return {
    ...route,
    isMosipVariant: false,
    isIsoVariant: false,
    isMosipBlocked: false,
    isRawVariant: false,
    isRawBlocked: false,
    isNistVariant: false,
    isNistBlocked: false,
    isMinexVariant: !isMinexBlocked && (exts.length === 0 || hasPlainImage || hasAnsiTemplate),
    isMinexBlocked,
    label: isMinexBlocked ? 'MINEX variant (blocked)' : 'MINEX variant',
    endpointPath,
    mosipVariantYesNo: false,
    blockedReason,
  };
};

/** @deprecated use normalizeApiVariant */
export const normalizeIsoNamespace = normalizeApiVariant;

export const normalizeFileExtension = (fileExt) =>
  String(fileExt || '').toLowerCase().replace(/^\./, '');

export const isPlainImageExtension = (ext) =>
  PLAIN_IMAGE_EXTENSIONS.has(normalizeFileExtension(ext));

export const isPlainImageForContext = (ext, contextKey) => {
  const normalized = normalizeFileExtension(ext);
  if (!normalized) return false;
  const formats = RAW_FORMATS_BY_CONTEXT[contextKey];
  if (!formats) return isPlainImageExtension(normalized);
  return formats.has(normalized);
};

export const resolveInputKind = (ext, contextKey) => {
  const normalized = normalizeFileExtension(ext);
  if (!normalized) return null;
  if (UNSUPPORTED_IN_CONTEXT[contextKey]?.has(normalized)) return 'unsupported';
  return isPlainImageForContext(normalized, contextKey) ? 'plain_image' : 'iso_record';
};

export const isIsoRecordInput = (ext, contextKey) =>
  resolveInputKind(ext, contextKey) === 'iso_record';

export const formatListLabel = (contextKey) => {
  const formats = RAW_FORMATS_BY_CONTEXT[contextKey];
  if (!formats) return RAW_FORMATS_COMMON.map((f) => f.toUpperCase()).join(', ');
  return [...formats].map((f) => f.toUpperCase()).join(', ');
};

export const buildAcceptAttribute = (apiVariant, contextKey) => {
  const plain = [...(RAW_FORMATS_BY_CONTEXT[contextKey] || toFormatSet(RAW_FORMATS_COMMON))]
    .map((ext) => `.${ext}`)
    .join(',');
  const variant = normalizeApiVariant(apiVariant);
  if (variant === API_VARIANT_NIST) {
    return `${plain},.ppm,.pgm,.bin,.iso`;
  }
  if (variant === API_VARIANT_MINEX) {
    return MINEX_FORMATS_FINGER.map((ext) => `.${ext}`).join(',');
  }
  if (variant === API_VARIANT_RAW) return plain;
  return `${plain},.iso,.bin`;
};

/**
 * Resolve face extract URL for MOSIP vs processed-template modes.
 * @returns {{ url: string|null, blocked: boolean, reason: string|null, operationPath: string|null }}
 */
export const resolveFaceExtractUrl = (
  biosdkBaseUrl,
  mode,
  fileExt,
  apiVariant = API_VARIANT_MOSIP
) => {
  const ext = normalizeFileExtension(fileExt);
  const variant = normalizeApiVariant(apiVariant);
  const base = trimTrailingSlash(biosdkBaseUrl);
  const mosipContext = CONTEXT_FACE_EXTRACT_MOSIP;
  const processedContext = CONTEXT_FACE_EXTRACT_PROCESSED;

  if (!ext) {
    return { url: null, blocked: false, reason: null, operationPath: null };
  }

  if (variant === API_VARIANT_NIST) {
    if (resolveInputKind(ext, processedContext) === 'iso_record') {
      return { url: null, blocked: true, reason: NIST_PLAIN_IMAGE_ONLY_MESSAGE, operationPath: null };
    }
    if (isPlainImageForContext(ext, processedContext)) {
      const operationPath = 'extract-processed-template';
      return {
        url: `${buildBiosdkRawBaseUrl(base)}/${operationPath}`,
        blocked: false,
        reason: null,
        operationPath,
      };
    }
    return { url: null, blocked: true, reason: 'Unsupported file type.', operationPath: null };
  }

  if (variant === API_VARIANT_RAW && resolveInputKind(ext, mode === EXTRACTION_MODE_PROCESSED ? processedContext : mosipContext) === 'iso_record') {
    return { url: null, blocked: true, reason: RAW_BLOCKED_MESSAGE, operationPath: null };
  }

  if (mode === EXTRACTION_MODE_PROCESSED) {
    if (!isPlainImageForContext(ext, processedContext) && resolveInputKind(ext, processedContext) === 'plain_image') {
      return { url: null, blocked: true, reason: 'Unsupported file type.', operationPath: null };
    }
    if (isPlainImageForContext(ext, processedContext)) {
      const operationPath = 'extract-processed-template';
      return {
        url: `${buildBiosdkRawBaseUrl(base)}/${operationPath}`,
        blocked: false,
        reason: null,
        operationPath,
      };
    }
    if (resolveInputKind(ext, processedContext) === 'iso_record') {
      if (variant === API_VARIANT_MOSIP) {
        return {
          url: null,
          blocked: true,
          reason:
            'Processed extract is not exposed under /mosip/. Switch API variant to ISO or upload a plain image for /raw/.',
          operationPath: null,
        };
      }
      const operationPath = 'extract-processed-template';
      return {
        url: `${buildBiosdkIsoBaseUrl(base)}/${operationPath}`,
        blocked: false,
        reason: null,
        operationPath,
      };
    }
    return { url: null, blocked: true, reason: 'Unsupported file type.', operationPath: null };
  }

  // MOSIP extract (default)
  if (isPlainImageForContext(ext, mosipContext)) {
    const operationPath = 'extract-template';
    return {
      url: `${buildBiosdkRawBaseUrl(base)}/${operationPath}`,
      blocked: false,
      reason: null,
      operationPath,
    };
  }
  if (resolveInputKind(ext, mosipContext) === 'iso_record') {
    const operationPath = 'extract-template';
    const namespaceBase =
      variant === API_VARIANT_ISO ? buildBiosdkIsoBaseUrl(base) : buildBiosdkMosipBaseUrl(base);
    return {
      url: `${namespaceBase}/${operationPath}`,
      blocked: false,
      reason: null,
      operationPath,
    };
  }
  return { url: null, blocked: true, reason: 'Unsupported file type.', operationPath: null };
};

export const getStoredIsoNamespace = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return API_VARIANT_MOSIP;
  }
  return normalizeApiVariant(window.localStorage.getItem(ISO_NAMESPACE_STORAGE_KEY));
};

export const setStoredIsoNamespace = (namespace) => {
  const normalized = normalizeApiVariant(namespace);
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(ISO_NAMESPACE_STORAGE_KEY, normalized);
  }
  return normalized;
};

/**
 * @param {string} biosdkBaseUrl - e.g. https://host/biosdk-service
 * @param {string} operationPath - e.g. extract-template, process, match
 * @param {boolean} isRaw - when true, use .../biosdk-service/raw/<operationPath>
 * @param {'mosip'|'iso'|'raw'} apiVariant - namespace for non-raw (default: mosip)
 */
export const buildBiosdkOperationUrl = (
  biosdkBaseUrl,
  operationPath,
  isRaw = false,
  apiVariant = API_VARIANT_MOSIP
) => {
  const normalizedVariant = normalizeApiVariant(apiVariant);
  const base = isRaw
    ? buildBiosdkRawBaseUrl(biosdkBaseUrl)
    : (normalizedVariant === API_VARIANT_ISO
      ? buildBiosdkIsoBaseUrl(biosdkBaseUrl)
      : buildBiosdkMosipBaseUrl(biosdkBaseUrl));
  const path = String(operationPath || '').replace(/^\//, '');
  return `${base}/${path}`;
};

/**
 * Client-only observability: ISO vs raw route selection (not sent on the wire).
 */
export const logBiosdkUiRoute = (componentName, routeTag, url, meta = {}) => {
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug('[biosdk-ui]', { component: componentName, route: routeTag, url, ...meta });
  }
};

export const resolveInputType = (fileExt, contextKey) => {
  const kind = resolveInputKind(fileExt, contextKey);
  if (!kind) return null;
  return kind === 'iso_record' ? 'iso_record' : 'raw_image';
};

export const resolveInputTypeLabel = (fileExtensions = [], apiVariant = API_VARIANT_MOSIP, contextKey) => {
  const exts = fileExtensions.map(normalizeFileExtension).filter(Boolean);
  if (!exts.length) return null;

  const kinds = exts.map((e) => resolveInputKind(e, contextKey));
  const allIso = kinds.every((k) => k === 'iso_record');
  const allPlain = kinds.every((k) => k === 'plain_image');
  const anyPlain = kinds.some((k) => k === 'plain_image');
  const anyIso = kinds.some((k) => k === 'iso_record');
  const variant = normalizeApiVariant(apiVariant);

  if (allIso) {
    return {
      type: 'iso_record',
      label: 'Input: ISO record',
      hint: `ISO record uploads use the header variant (/mosip/ or /iso/). ${ISO_RECORD_LABEL}`,
    };
  }
  if (anyPlain && anyIso) {
    return {
      type: 'mixed',
      label: 'Input: Mixed (plain image + ISO record)',
      hint: 'Match uses /raw/ when any input is a plain image.',
    };
  }
  const formats = contextKey ? formatListLabel(contextKey) : 'plain images';
  if (variant === API_VARIANT_NIST) {
    return {
      type: 'plain_image',
      label: 'Input: Plain image',
      hint: `NIST / FRVT path · plain images → /raw/… · ${formats}`,
    };
  }
  if (variant === API_VARIANT_MINEX) {
    const allAnsi = exts.every((e) => isMinexAnsiTemplateExtension(e));
    if (allAnsi) {
      return {
        type: 'ansi_template',
        label: 'Input: ANSI 378 template',
        hint: `MINEX path · ${MINEX_ANSI_TEMPLATE_LABEL}`,
      };
    }
    return {
      type: 'plain_image',
      label: 'Input: Plain image',
      hint: `MINEX / ANSI 378 path · plain images → /minex/extract-template · ${formats}`,
    };
  }
  if (variant === API_VARIANT_RAW) {
    return {
      type: 'plain_image',
      label: 'Input: Plain image',
      hint: `Raw variant · supported formats: ${formats}`,
    };
  }
  return {
    type: 'plain_image',
    label: 'Input: Plain image',
    hint: `Plain image uploads route to /raw/. Supported: ${formats}`,
  };
};

export const resolveBiosdkRouteUrl = (
  biosdkBaseUrl,
  operationPath,
  fileExtensions,
  apiVariant = API_VARIANT_MOSIP,
  contextKey
) => {
  const exts = (fileExtensions || []).map(normalizeFileExtension).filter(Boolean);
  const isRaw = exts.length === 0
    ? false
    : exts.some((e) => isPlainImageForContext(e, contextKey));
  const namespace = isRaw ? API_VARIANT_RAW : normalizeApiVariant(apiVariant);
  const url = buildBiosdkOperationUrl(biosdkBaseUrl, operationPath, isRaw, apiVariant);
  return { url, namespace, isRaw, operationPath };
};

export const resolveVariantStatus = (
  apiVariant,
  fileExtensions,
  operationPath,
  biosdkBaseUrl = '',
  contextKey
) => {
  const variant = normalizeApiVariant(apiVariant);
  const route = resolveBiosdkRouteUrl(
    biosdkBaseUrl,
    operationPath,
    fileExtensions,
    apiVariant,
    contextKey
  );
  const exts = (fileExtensions || []).map(normalizeFileExtension).filter(Boolean);
  const isMosipVariant = route.namespace === API_VARIANT_MOSIP && !route.isRaw;
  const isIsoVariant = route.namespace === API_VARIANT_ISO && !route.isRaw;
  const isMosipBlocked = variant === API_VARIANT_MOSIP && route.isRaw && exts.length > 0;
  const isRawVariant = variant === API_VARIANT_RAW && route.isRaw && exts.length > 0;
  const isRawBlocked = variant === API_VARIANT_RAW && !route.isRaw && exts.length > 0;
  const endpointPath = route.url
    ? route.url.replace(trimTrailingSlash(biosdkBaseUrl), '')
    : `/${route.namespace}/${operationPath}`;

  let label = 'Raw variant';
  if (isMosipVariant) label = 'MOSIP variant';
  else if (isIsoVariant) label = 'ISO variant';
  else if (isRawVariant) label = 'Raw variant';

  return {
    ...route,
    isMosipVariant,
    isIsoVariant,
    isMosipBlocked,
    isRawVariant,
    isRawBlocked,
    label,
    endpointPath,
    mosipVariantYesNo: isMosipVariant,
    blockedReason: isRawBlocked ? RAW_BLOCKED_MESSAGE : null,
  };
};

export const buildRequestSummary = ({
  variantStatus,
  inputTypeLabel,
  operation,
  inputs,
  extra = {},
}) => {
  if (!variantStatus) return null;
  return {
    variant: variantStatus.label,
    isMosipVariant: variantStatus.isMosipVariant,
    mosipVariantYesNo: variantStatus.isMosipVariant,
    endpoint: variantStatus.endpointPath,
    inputType: inputTypeLabel?.label || null,
    operation,
    inputs,
    extra,
  };
};

export const EXTRACT_VARIANT_GUIDE_ROWS = [
  { variant: 'MOSIP', input: 'ISO record (.iso, .bin)', header: 'mosip', endpoint: '/mosip/extract-template', highlight: true },
  { variant: 'ISO', input: 'ISO record (.iso, .bin)', header: 'iso', endpoint: '/iso/extract-template', highlight: false },
  { variant: 'Raw', input: 'JPG, JPEG, PNG, BMP, JP2', header: 'raw', endpoint: '/raw/extract-template', highlight: false },
  { variant: 'NIST', input: 'Face: JPG/PNG/BMP · Iris: JPG/PNG/BMP', header: 'nist', endpoint: '/raw/extract-processed-template or /raw/extract-template', highlight: false },
  { variant: 'MINEX', input: 'Fingerprint: JPG/PNG/BMP/WSQ → ANSI 378', header: 'minex', endpoint: '/minex/extract-template', highlight: false },
];

export const MATCH_VARIANT_GUIDE_ROWS = [
  { variant: 'MOSIP', input: 'both ISO records', header: 'mosip', endpoint: '/mosip/process', highlight: true },
  { variant: 'ISO', input: 'both ISO records', header: 'iso', endpoint: '/iso/process', highlight: false },
  { variant: 'Raw', input: 'plain images (any slot)', header: 'raw', endpoint: '/raw/process', highlight: false },
  {
    variant: 'NIST',
    input: '1:1 or 1:N — BCHQ/BCHI .bin or plain images (gallery[] on /raw/match)',
    header: 'nist',
    endpoint: '/raw/match',
    highlight: false,
  },
  {
    variant: 'MINEX',
    input: '1:1 — ANSI 378 .bin templates or plain images (extract then match)',
    header: 'minex',
    endpoint: '/minex/match',
    highlight: false,
  },
];

export const NIST_MATCH_1N_GUIDE_ROWS = [
  {
    variant: 'NIST 1:N face',
    input: 'probe image/BCHQ + N gallery images or .bin',
    header: 'nist',
    endpoint: '/raw/match (gallery[])',
    highlight: true,
  },
  {
    variant: 'NIST 1:N face M×N',
    input: 'group photo + N gallery → M×N similarity matrix',
    header: 'nist',
    endpoint: '/raw/match',
    highlight: false,
  },
  {
    variant: 'NIST 1:N iris',
    input: 'probe + N gallery; rank ascending dissimilarity',
    header: 'nist',
    endpoint: '/raw/match (gallery[])',
    highlight: false,
  },
];

export const QUALITY_VARIANT_GUIDE_ROWS = [
  { variant: 'MOSIP', input: 'ISO record (.iso, .bin)', header: 'mosip', endpoint: '/mosip/process', highlight: true },
  { variant: 'ISO', input: 'ISO record (.iso, .bin)', header: 'iso', endpoint: '/iso/process', highlight: false },
  { variant: 'Raw', input: 'JPG, JPEG, PNG, BMP', header: 'raw', endpoint: '/raw/process', highlight: false },
  { variant: 'NIST', input: 'not supported', header: 'nist', endpoint: '—', highlight: false },
  { variant: 'MINEX', input: 'not supported', header: 'minex', endpoint: '—', highlight: false },
];

export const API_VARIANT_HINTS = {
  [API_VARIANT_RAW]: 'Plain images → /raw/… · see supported formats on each page',
  [API_VARIANT_MOSIP]: 'MOSIP variant · ISO19794 record → /mosip/… (e.g. .iso, .bin)',
  [API_VARIANT_ISO]: 'ISO namespace · ISO19794 record → /iso/… (e.g. .iso, .bin)',
  [API_VARIANT_NIST]:
    'FRVT template path · Face BCHQ + Iris BCHI · 1:1 and 1:N match via /raw/match gallery[]',
  [API_VARIANT_MINEX]:
    'MINEX III fingerprint · ANSI INCITS 378-2004 · extract + 1:1 match via /minex/…',
};
